import React, { useState, useRef, useEffect } from 'react'
import { TaskBoardType, TaskColumn as TaskColumnType } from '../../types/task-board'
import { State } from '../../types/state'
import { TaskColumnCard } from './TaskColumnCard'
import { getUserColor } from '../../utils/userColors'

interface TaskColumnProps {
  column: TaskColumnType
  board: TaskBoardType
  filterMode: 'all' | 'assigned'
  userState: State['user']
  onAddCard: (columnId: string) => void
  onDeleteColumn: (columnId: string) => void
  onUpdateColumnTitle: (columnId: string, title: string) => void
  onCardClick: (columnId: string, cardId: string) => void
  onPriorityClick: (columnId: string, cardId: string) => void
  onDueDateClick: (columnId: string, cardId: string) => void
  onDependencyClick: (columnId: string, cardId: string) => void
  onMoveCard: (cardId: string, columnId: string, direction: 'up' | 'down') => void
  onChangeCardPosition: (cardId: string, columnId: string, direction: 'up' | 'down') => void
  onAssignUser: (cardId: string, userId: string | null) => Promise<void>
}

export const TaskColumn: React.FC<TaskColumnProps> = ({
  column,
  board,
  filterMode,
  userState,
  onAddCard,
  onDeleteColumn,
  onUpdateColumnTitle,
  onCardClick,
  onPriorityClick,
  onDueDateClick,
  onDependencyClick,
  onMoveCard,
  onChangeCardPosition,
  onAssignUser
}) => {
  const [editingAssigneeId, setEditingAssigneeId] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [title, setTitle] = useState(column.title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && (inputRef.current != null)) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleTitleClick = () => {
    setIsEditing(true)
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value)
  }

  const handleTitleBlur = () => {
    if (title.trim() !== column.title) {
      onUpdateColumnTitle(column.id, title.trim())
    }
    setIsEditing(false)
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (title.trim() !== column.title) {
        onUpdateColumnTitle(column.id, title.trim())
      }
      setIsEditing(false)
    } else if (e.key === 'Escape') {
      setTitle(column.title)
      setIsEditing(false)
    }
  }

  // Funkcja do obsługi kliknięcia poza selectem
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const select = document.getElementById('assignee-select-' + editingAssigneeId)
      if ((select != null) && !select.contains(event.target as Node)) {
        setEditingAssigneeId(null)
      }
    }
    if (editingAssigneeId) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editingAssigneeId])

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 0: return '#6c757d' // Normalny - szary
      case 1: return '#28a745' // Niski - zielony
      case 2: return '#17a2b8' // Średni - niebieski
      case 3: return '#ffc107' // Wysoki - żółty
      case 4: return '#dc3545' // Krytyczny - czerwony
      default: return '#6c757d'
    }
  }

  return (
    <div className='task-column mr-20' style={{ minWidth: '300px', maxWidth: '300px' }}>
      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center bg-light'>
          <h5 className='mb-0' style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 70px)' }}>
            {isEditing
              ? (
                <input
                  ref={inputRef}
                  type='text'
                  className='form-control'
                  value={title}
                  onChange={handleTitleChange}
                  onBlur={handleTitleBlur}
                  onKeyDown={handleTitleKeyDown}
                />
                )
              : (
                <h5 onClick={handleTitleClick} style={{ cursor: 'pointer' }}>
                  {column.title}
                </h5>
                )}
          </h5>
          <div className='d-flex task-column-header-icons'>
            <i
              className='fa fa-plus task-column-add-icon'
              style={{
                fontSize: '12px',
                color: '#6c757d',
                cursor: 'pointer',
                opacity: 0.8,
                transition: 'opacity 0.2s ease',
                marginLeft: '10px',
                marginRight: column.title !== 'Do zrobienia' && column.title !== 'W trakcie' && column.title !== 'Wykonane' ? '8px' : '0'
              }}
              onClick={(e) => {
                e.stopPropagation()
                onAddCard(column.id)
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
              title='Dodaj zadanie'
            />
            {column.title !== 'Do zrobienia' && column.title !== 'W trakcie' && column.title !== 'Wykonane' && (
              <i
                className='fa fa-trash'
                style={{
                  fontSize: '12px',
                  color: '#dc3545',
                  cursor: 'pointer',
                  opacity: 0.8,
                  transition: 'opacity 0.2s ease'
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  if (window.confirm('Czy na pewno chcesz usunąć tę kolumnę? Wszystkie zadania w tej kolumnie zostaną usunięte.')) {
                    onDeleteColumn(column.id)
                  }
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                title='Usuń kolumnę'
              />
            )}
          </div>
        </div>
        <div
          className='card-body'
          style={{ minHeight: '50vh' }}
        >
          {column.cards
            .filter(card => filterMode === 'all' || ((card.assignedTo != null) && String(card.assignedTo.id) === String(userState.id)))
            .map((card, index) => (
              <div
                key={card.id}
                className='card task-card mb-20'
                onClick={() => onCardClick(column.id, card.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className='card-body'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <h6 className='card-title mb-0'>{card.title}</h6>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <i
                        className='fa fa-flag'
                        style={{
                          fontSize: '12px',
                          color: getPriorityColor(card.priority ?? 0),
                          cursor: 'pointer',
                          opacity: 0.8,
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                        onClick={(e) => {
                          e.stopPropagation()
                          onPriorityClick(column.id, card.id)
                        }}
                        title={`Priorytet: ${card.priority ?? 0}`}
                      />
                      <i
                        className={`fa ${card.dueDate ? 'fa-calendar-check' : 'fa-calendar'}`}
                        style={{
                          fontSize: '12px',
                          color: card.dueDate ? '#28a745' : '#6c757d',
                          cursor: 'pointer',
                          opacity: 0.8,
                          transition: 'opacity 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                        onClick={(e) => {
                          e.stopPropagation()
                          onDueDateClick(column.id, card.id)
                        }}
                        title={card.dueDate ? `Termin: ${new Date(card.dueDate).toLocaleString()}` : 'Ustaw termin'}
                      />
                      <i
                        className='fa fa-link'
                        style={{
                          fontSize: '12px',
                          color: ((card.dependencies != null) && card.dependencies.length > 0) ? '#17a2b8' : '#6c757d',
                          cursor: 'pointer',
                          opacity: 0.8,
                          transition: 'opacity 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          onDependencyClick(column.id, card.id)
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                        title={`Powiązania${(card.dependencies != null) && card.dependencies.length > 0 ? ` (${card.dependencies.length})` : ''}`}
                      />
                    </div>
                  </div>
                  <p className='card-text small text-muted'>
                    {card.description.length > 100
                      ? `${card.description.substring(0, 100)}...`
                      : card.description}
                  </p>

                  {/* Przypisany użytkownik - avatar + select po kliknięciu */}
                  <div className='mt-10 d-flex align-items-center'>
                    <span className='small text-muted mr-10'>Przypisano:</span>
                    {editingAssigneeId === card.id
                      ? (
                        <select
                          id={'assignee-select-' + card.id}
                          className='form-select form-select-sm'
                          value={(card.assignedTo != null) ? card.assignedTo.id : ''}
                          autoFocus
                          onClick={e => e.stopPropagation()}
                          onChange={async (e) => {
                            e.stopPropagation()
                            const userId = e.target.value || null
                            await onAssignUser(card.id, userId !== '' ? userId : null)
                            setEditingAssigneeId(null)
                          }}
                          onBlur={() => setEditingAssigneeId(null)}
                          style={{ minWidth: 120, fontSize: 13, marginLeft: 8 }}
                        >
                          <option value=''>Brak</option>
                          {board.members.map(member => (
                            <option key={member.id} value={member.id}>{member.username}</option>
                          ))}
                        </select>
                        )
                      : (
                        <div
                          className='d-flex align-items-center'
                          style={{ cursor: 'pointer' }}
                          onClick={e => {
                            e.stopPropagation()
                            setEditingAssigneeId(card.id)
                          }}
                        >
                          {(card.assignedTo != null)
                            ? (
                              <>
                                <div
                                  className='rounded-circle mr-10 d-flex align-items-center justify-content-center'
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    backgroundColor: getUserColor(card.assignedTo.username),
                                    color: 'white',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                  }}
                                  title={card.assignedTo.username}
                                >
                                  {card.assignedTo.username.charAt(0).toUpperCase()}
                                </div>
                              </>
                              )
                            : (
                              <span className='small text-muted'>Brak</span>
                              )}
                        </div>
                        )}
                  </div>

                  {card.comments.length > 0 && (
                    <div className='mt-10'>
                      <span className='small text-muted'>
                        <i className='fa fa-comment mr-1' />
                        {card.comments.length}
                      </span>
                    </div>
                  )}

                  <div className='mt-2 d-flex justify-content-end' style={{ gap: '4px' }}>
                    <i
                      className='fa fa-arrow-up'
                      style={{
                        fontSize: '12px',
                        color: index === 0 ? '#dee2e6' : '#6c757d',
                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                        opacity: index === 0 ? 0.5 : 0.8,
                        transition: 'opacity 0.2s ease'
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (index !== 0) {
                          onChangeCardPosition(card.id, column.id, 'up')
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (index !== 0) {
                          e.currentTarget.style.opacity = '1'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (index !== 0) {
                          e.currentTarget.style.opacity = '0.8'
                        }
                      }}
                      title='Przesuń w górę'
                    />
                    <i
                      className='fa fa-arrow-down'
                      style={{
                        fontSize: '12px',
                        color: index === column.cards.length - 1 ? '#dee2e6' : '#6c757d',
                        cursor: index === column.cards.length - 1 ? 'not-allowed' : 'pointer',
                        opacity: index === column.cards.length - 1 ? 0.5 : 0.8,
                        transition: 'opacity 0.2s ease'
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (index !== column.cards.length - 1) {
                          onChangeCardPosition(card.id, column.id, 'down')
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (index !== column.cards.length - 1) {
                          e.currentTarget.style.opacity = '1'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (index !== column.cards.length - 1) {
                          e.currentTarget.style.opacity = '0.8'
                        }
                      }}
                      title='Przesuń w dół'
                    />
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
